import {TargetBlank} from '@wandb/weave/common/util/links';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {FC, Fragment, useContext} from 'react';

import {TeamOrgQuery} from '../../../generated/graphql';
import {
  createDismissibleBanner,
  DEFAULT_BANNER_SHOW_AFTER,
} from '../../../pages/Billing/AccountSettings/WarningBanner/DismissibleBanner';
import {Analytics} from '../../../services/analytics';
import {useCurrentTeamOrg} from '../../../util/nudgeBars';
import {accountSettingsUsagePage} from '../../../util/urls';
import {
  Banner,
  BannerBoldText,
  BannerFlexWrapper,
  BannerTextWrapper,
} from '../../Banner';
import {GlobalBannerContext} from '../GlobalBannerContextProvider';

export type ResourceSubscriptionHasBeenUpgradedBannerProps = {
  organizationId: string;
  organizationName: string;
  resource: 'storage' | 'artifact';
  orgStorageBytesLimitGB: number;
};

export const ResourceSubscriptionHasBeenUpgradedBannerGlobalList: FC = () => {
  const currentTeamOrg: TeamOrgQuery | null = useCurrentTeamOrg();

  const {states} = useContext(GlobalBannerContext);

  if (states == null || states.length === 0) {
    return null;
  }

  const organizationStatesToShow = currentTeamOrg
    ? states?.filter(
        state =>
          state.account.name === currentTeamOrg?.entity?.organization?.name
      )
    : states;

  if (organizationStatesToShow == null) {
    return null;
  }

  return (
    <Tailwind>
      <div className="mb-8 space-y-8">
        {organizationStatesToShow.map(state => {
          const storageSubscriptionHasBeenUpgradedBannerProps =
            state.storageAndEnforcementBannerFlags
              .storageSubscriptionHasBeenUpgradedBannerProps;

          const artifactStorageSubscriptionHasBeenUpgradedBannerProps =
            state.storageAndEnforcementBannerFlags
              .artifactStorageSubscriptionHasBeenUpgradedBannerProps;

          return (
            <Fragment key={state.account.name + state.account.accountType}>
              {storageSubscriptionHasBeenUpgradedBannerProps && (
                <ResourceSubscriptionHasBeenUpgradedBanner
                  {...storageSubscriptionHasBeenUpgradedBannerProps}
                />
              )}

              {artifactStorageSubscriptionHasBeenUpgradedBannerProps && (
                <ResourceSubscriptionHasBeenUpgradedBanner
                  {...artifactStorageSubscriptionHasBeenUpgradedBannerProps}
                />
              )}
            </Fragment>
          );
        })}
      </div>
    </Tailwind>
  );
};

export const ResourceSubscriptionHasBeenUpgradedBanner =
  createDismissibleBanner<ResourceSubscriptionHasBeenUpgradedBannerProps>({
    id: ({organizationId, resource}) =>
      'upgrade-success:' + organizationId + ':' + resource,
    showAfter: DEFAULT_BANNER_SHOW_AFTER,
    Component: ({
      onDismiss,
      organizationId,
      organizationName,
      resource,
      orgStorageBytesLimitGB,
    }) => {
      return (
        <Tailwind>
          <Banner
            variant={'info'}
            eventData={{
              location: `${resource} subscription has been upgraded banner`,
              organizationId,
              resource,
            }}>
            <BannerFlexWrapper>
              <div>
                <Icon name="lightbulb-info" />
              </div>
              <BannerTextWrapper>
                <span>
                  <BannerBoldText>
                    You have exceeded {orgStorageBytesLimitGB} GB of {resource}{' '}
                    data.{' '}
                  </BannerBoldText>
                  Your plan is now pay-as-you-go.{' '}
                  <TargetBlank
                    href={accountSettingsUsagePage(organizationName)}
                    className="font-semibold text-moon-800 hover:text-moon-950">
                    Manage your usage{' '}
                  </TargetBlank>{' '}
                  to return under the free {resource} limit. If you have deleted
                  storage to stay under {orgStorageBytesLimitGB} GB, changes may
                  take up to 1 hour to reflect in wandb.
                </span>
              </BannerTextWrapper>
            </BannerFlexWrapper>
            <button
              className="flex items-center border-none bg-transparent font-semibold text-moon-800 hover:cursor-pointer hover:text-moon-950"
              onClick={() => {
                Analytics.track('Dismiss button clicked', {
                  location: `${resource} subscription has been upgraded banner`,
                  organizationId,
                  resource,
                });
                onDismiss();
              }}>
              Dismiss
            </button>
          </Banner>
        </Tailwind>
      );
    },
  });
